import moment from 'moment';
import states from './bookingStates';

const log = {
  debug: () => undefined,
};

const priorityLow = 10;
const priorityNormal = 20;
const priorityHigh = 30;

function findLeg(trips, tripType, legType) {
  const activeTrip = trips.find(
    (trip) => (trip.status === 'active' || trip.status === 'complete') && trip.type === tripType
  );
  if (activeTrip) {
    return activeTrip.legs.find((leg) => leg.direction === legType);
  }
  return null;
}

function getDetailedStatusName(booking) {
  const { masterStatusId, trips, garageStatus, allocationStatus, collectionDeliveryOption } = booking;
  const active = trips.find((trip) => trip.type === 'customer-garage' && trip.status === 'active');
  let friendlyName, failedTrip, leg;
  let id = masterStatusId * 100;
  switch (masterStatusId) {
    case states.UNALLOCATED:
    case states.GARAGE_DECLINED:
    case states.ALLOCATED:
      friendlyName = 'To allocate';
      break;
    case states.PENDING:
      friendlyName = allocationStatus === 'review' ? 'To review' : 'Pending answer';
      break;
    case states.AWAITING_PICKUP:
      failedTrip = trips.find((trip) => trip.type === 'customer-garage' && trip.status === 'failed');
      if (failedTrip && !active) friendlyName = 'Collection failed';
      else if (collectionDeliveryOption === 'CustomerC&D') friendlyName = 'Awaiting customer drop off';
      else friendlyName = 'Waiting for pick up';
      break;
    case states.BEING_COLLECTED:
      leg = findLeg(trips, 'customer-garage', 'inbound');
      if (leg) {
        friendlyName = leg.status === 'complete' ? 'Customer hand over' : 'On way to customer';
        id = leg.status === 'complete' ? masterStatusId * 100 + 40 : masterStatusId * 100 + 20;
      } else {
        log.debug('No active leg for this booking at BEING_COLLECTED');
        friendlyName = 'On way to customer';
      }
      break;
    case states.TRANSPORT_TO_GARAGE:
      leg = findLeg(trips, 'customer-garage', 'outbound');
      if (leg) {
        friendlyName = leg.status === 'complete' ? 'Garage hand over' : 'On way to garage';
        id = leg.status === 'complete' ? masterStatusId * 100 + 40 : masterStatusId * 100 + 20;
      } else {
        log.debug('No active leg for this booking at TRANSPORT TO GARAGE');
        friendlyName = 'On way to garage';
      }
      break;
    case states.AT_GARAGE:
      if (garageStatus === 'FIXTER_REVIEW') friendlyName = 'To review';
      else if (garageStatus === 'CUSTOMER_REVIEW') friendlyName = 'Pending approval';
      else if (garageStatus === 'CUSTOMER_REVIEWED') friendlyName = 'Customer reviewed';
      else if (garageStatus === 'GARAGE_DRAFT') friendlyName = 'Quoting extra work';
      else friendlyName = 'Work in progress';
      break;
    case states.WORK_COMPLETE:
      friendlyName = 'Take payment';
      break;
    case states.PAID:
      leg = findLeg(trips, 'garage-customer', 'inbound');
      if (leg) {
        switch (leg.status) {
          case 'notstarted':
            friendlyName = 'Waiting for pick up';
            id = masterStatusId * 100 + 20;
            break;
          case 'complete': {
            const outboundLeg = findLeg(trips, 'garage-customer', 'outbound');
            if (outboundLeg && outboundLeg.status === 'started') {
              friendlyName = 'On way to customer';
              id = masterStatusId * 100 + 20;
            } else if (outboundLeg && outboundLeg.status === 'complete') {
              friendlyName = 'Customer hand over';
              id = masterStatusId * 100 + 40;
            } else {
              friendlyName = 'Garage hand over';
              id = masterStatusId * 100 + 60;
            }
            break;
          }
          default: {
            if (collectionDeliveryOption === 'CustomerC&D') friendlyName = 'Awaiting customer collection';
            else friendlyName = 'On way to garage';
            id = masterStatusId * 100 + 40;
            break;
          }
        }
      } else {
        log.debug('No active leg for this booking at BEING_RETURNED');
        friendlyName = 'On way to garage';
      }
      failedTrip = trips.find((trip) => trip.type === 'garage-customer' && trip.status === 'failed');
      if (failedTrip && !leg) friendlyName = 'Delivery Failed';
      break;
    case states.BEING_RETURNED:
      leg = findLeg(trips, 'garage-customer', 'outbound');
      if (leg) {
        friendlyName = leg.status === 'complete' ? 'Customer hand over' : 'On way to customer';
        id = leg.status === 'complete' ? masterStatusId * 100 + 40 : masterStatusId * 100 + 20;
      } else {
        log.debug('No active leg for this booking at BEING_RETURNED');
        friendlyName = 'On way to customer';
      }
      failedTrip = trips.find((trip) => trip.type === 'garage-customer' && trip.status === 'failed');
      if (failedTrip && !leg) friendlyName = 'Delivery Failed';
      break;
    case states.CLOSED:
      friendlyName = 'Closed';
      break;
    case states.UNPAID:
      friendlyName = 'Unpaid';
      break;
    case states.CANCELLED:
      friendlyName = 'Cancelled';
      break;
    case states.DISPUTE:
      friendlyName = 'DISPUTE';
      break;
    case states.INCOMPLETE:
      friendlyName = 'INCOMPLETE';
      break;
    case states.BOOKING_CHANGE:
      friendlyName = 'BOOKING_CHANGE';
      break;
    default:
      friendlyName = 'Unknown';
      break;
  }
  return { id, friendlyName };
}

function isTheGarageCollectingTheVehicle(garageInvites) {
  if (garageInvites) {
    const activeInvitation = garageInvites.find((b) => b.garage && b.garage.status === 'ACTIVE');
    return activeInvitation && activeInvitation.garage.garageDeliveryOption === 'GarageC&D';
  }
  return false;
}

function isDriverLate(timeToArrive, minutesBefore) {
  const arrivalTime = moment(timeToArrive).subtract(minutesBefore, 'minutes');
  const now = moment();
  return arrivalTime.isBefore(now);
}
// high priority if booking is today or tomorrow
function isGaragePriorityHigh(bookingDate) {
  let SUB_HOURS = 48;
  const dayOfWeek = moment().day();
  // if it's Friday, a Monday booking receive high priority
  if (dayOfWeek === 5) SUB_HOURS = 96;
  const bookingDay = moment(bookingDate).subtract(SUB_HOURS, 'hours');
  const now = moment().startOf('day');
  return bookingDay.isBefore(now);
}
function areDriversAllocated(trips) {
  if (trips) {
    const unallocatedDriver = trips.find((b) => b.pickerId === null);
    return !unallocatedDriver;
  }
  return false;
}
// high priority if booking is today or before tomorrow at 16:00
function isDriverAllocationPriorityHigh(bookingDate) {
  const bookingDay = moment(bookingDate);
  const now = moment().startOf('day').add(40, 'hours');
  return bookingDay.isBefore(now);
}

function getDetailedStatusPriority(bookingAll, friendlyStatus) {
  const { masterStatusId } = bookingAll;
  // higher priority if Fixter is doing collection and delivery
  const offset = isTheGarageCollectingTheVehicle(bookingAll.garageInvites) ? 5 : 0;
  let priority;
  switch (masterStatusId) {
    case states.UNALLOCATED:
    case states.GARAGE_DECLINED:
      priority = isGaragePriorityHigh(bookingAll.collectionDateTimeLocal) ? priorityHigh : priorityNormal;
      break;
    case states.PENDING:
      priority = isGaragePriorityHigh(bookingAll.collectionDateTimeLocal) ? priorityHigh : priorityLow;
      break;
    case states.ALLOCATED:
      if (areDriversAllocated(bookingAll.trips)) {
        priority = priorityNormal;
      } else {
        priority = isDriverAllocationPriorityHigh(bookingAll.collectionDateTimeLocal)
          ? priorityHigh
          : priorityNormal;
      }
      break;
    case states.AWAITING_PICKUP:
      if (friendlyStatus === 'Collection failed') {
        priority = priorityHigh;
      } else if (friendlyStatus === 'Awaiting customer drop off') {
        priority = priorityLow - offset;
      } else {
        priority = isDriverLate(bookingAll.collectionDateTimeLocal, 15)
          ? priorityHigh - offset
          : priorityLow - offset;
      }
      break;
    case states.BEING_COLLECTED:
      priority =
        friendlyStatus === 'On way to customer' && isDriverLate(bookingAll.collectionDateTimeLocal, 0)
          ? priorityHigh - offset
          : priorityLow - offset;
      break;
    case states.AT_GARAGE:
      if (isDriverLate(bookingAll.deliveryDateTimeLocal, 45) || friendlyStatus === 'Customer reviewed')
        priority = priorityHigh;
      else if (friendlyStatus === 'To review' || friendlyStatus === 'Pending approval')
        priority = priorityNormal;
      else priority = priorityLow;
      break;
    case states.WORK_COMPLETE:
      priority = priorityNormal;
      break;
    case states.PAID:
      if (friendlyStatus === 'Waiting for pick up') {
        priority = isDriverLate(bookingAll.deliveryDateTimeLocal, 45)
          ? priorityHigh - offset
          : priorityLow - offset;
      } else if (friendlyStatus === 'On way to garage') {
        priority = isDriverLate(bookingAll.deliveryDateTimeLocal, 30)
          ? priorityHigh - offset
          : priorityLow - offset;
      } else if (friendlyStatus === 'Garage hand over') {
        priority = isDriverLate(bookingAll.deliveryDateTimeLocal, 15)
          ? priorityHigh - offset
          : priorityLow - offset;
      } else if (friendlyStatus === 'Delivery Failed') {
        priority = priorityHigh;
      } else if (friendlyStatus === 'Awaiting customer collection') {
        priority = priorityLow - offset;
      } else {
        priority = priorityHigh - offset;
      }
      break;
    case states.BEING_RETURNED:
      priority =
        friendlyStatus === 'On way to customer' && isDriverLate(bookingAll.deliveryDateTimeLocal, 0)
          ? priorityHigh
          : priorityLow;
      if (friendlyStatus === 'Delivery Failed') priority = priorityHigh;
      break;
    case states.UNPAID:
      priority = priorityHigh;
      break;
    default:
      priority = priorityLow;
      break;
  }
  return priority;
}

export default function (bookingAll) {
  if (bookingAll.masterStatusId) {
    const idAndName = getDetailedStatusName(bookingAll);
    return { ...idAndName, priority: getDetailedStatusPriority(bookingAll, idAndName.friendlyName) };
  }
  log.debug('No masterStatusId to create detailedStatus');
  return {};
}
